import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Divider,
    IconButton,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Control, useWatch } from "react-hook-form";

import {
    bcbsTcRetroModal,
    DisciplineCell,
    IconButtonColor,
    RetroCancelButton,
    RetroConfirmButton,
    RetroTableCellHeader,
    VisitTextField,
    VisitTextFieldOnChange,
} from "../styles/style";
import { Discipline } from "../../../models/Master";
import { IsRetroDetails } from "../../../models/Referral";
import { getValue } from "../../../utils";
import { flexAllCentre } from "../../../styles/mui/styles/display";
import fontWeight from "../../../styles/mui/fontWeight";
import {
    FiMinusCircleIcon,
    FiPlusCircleIcon,
} from "../../ReAuthWorkFlow/styles/style";
import { Numbers } from "../../../constants/Referral";
import colors from "../../../styles/colors/colors";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { RETRO_MODAL } from "../../../constants/ResponseMessage";
import { TabSessionStorageResponse } from "../../../models/api/TabSessionStorage";

export interface PropsFromState {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    disciplineListState: Discipline[];
    getValues: any;
    setValue: any;
    isRetroDetailsState: IsRetroDetails;
    control: Control<any>;
    tabSessionStorage: TabSessionStorageResponse;
}

const BcbsTnRetroModal: React.FC<PropsFromState> = ({
    open,
    onClose,
    onConfirm,
    setValue,
    disciplineListState,
    getValues,
    isRetroDetailsState,
    control,
    tabSessionStorage,
}) => {
    const [modalError, setModalError] = useState("");

    const selectedIds: number[] = Array.isArray(
        getValues("requestService.discipline")
    )
        ? getValues("requestService.discipline")
        : [];

    const selectedDisciplines = (disciplineListState || []).filter((d) =>
        selectedIds.includes(d.disciplineId)
    );

    const getVisitPath = (id: number) =>
        `requestService.referralDisciplineDto.${id}.acceptedVisits`;

    const normalizeValue = (v: any) =>
        v === "" || v === undefined || v === null ? 0 : Number(v);

    useEffect(() => {
        if (!open) return;

        selectedDisciplines.forEach(({ disciplineId }) => {
            const path = getVisitPath(disciplineId);
            const value = getValues(path);

            if (value === undefined || value === null || value === "") {
                setValue(path, 0, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }
        });
    }, [open]);

    const handleConfirm = () => {
        const hasAtLeastOneVisit = selectedDisciplines.some((d) => {
            const value = normalizeValue(getValues(getVisitPath(d.disciplineId)));
            return Number.isInteger(value) && value >= 1;
        });
        if (!hasAtLeastOneVisit) {
            setModalError(RETRO_MODAL);
            return;
        }
        setModalError("");
        onConfirm();
    };

    const handleCloseModal = () => {
        const sessionVisits = getValue(
            tabSessionStorage,
            "response.requestService.referralDisciplineDto"
        );
        setValue(
            "requestService.referralDisciplineDto",
            sessionVisits?.length ? sessionVisits : [],
        );
        onClose();
        setModalError("");
    };

    const watchedVisits = useWatch({
        control,
        name: selectedDisciplines.map((d) => getVisitPath(d.disciplineId)),
    });

    const handleCounter = (disciplineId: number, delta: number) => {
        const path = getVisitPath(disciplineId);
        const current = normalizeValue(getValues(path));
        let next = Math.max(0, current + delta);
        if (next > 9999) next = 9999;
        setValue(path, next, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });
        if (modalError && next > 0) {
            setModalError("");
        }
    };

    const renderVisitInput = (disciplineId: number, index: number) => {
        const raw = watchedVisits?.[index];
        const value = String(normalizeValue(raw));

        return (
            <>
                <IconButton
                    onClick={() => handleCounter(disciplineId, -1)}
                    sx={IconButtonColor}
                >
                    <FiMinusCircleIcon />
                </IconButton>

                <TextField
                    variant="standard"
                    size="medium"
                    value={value}
                    inputProps={{
                        min: Numbers.ZERO,
                        max: 9999,
                        maxLength: Numbers.Four,
                        sx: VisitTextField,
                    }}
                    onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, "");
                        if (v.length > Numbers.Four) v = v.slice(0, Numbers.Four);

                        setTimeout(() => {
                            setValue(getVisitPath(disciplineId), normalizeValue(v), {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true,
                            });
                        }, 100);

                        if (modalError) setModalError("");
                    }}
                    sx={VisitTextFieldOnChange}
                />

                <IconButton
                    onClick={() => handleCounter(disciplineId, Numbers.ONE)}
                    sx={IconButtonColor}
                >
                    <FiPlusCircleIcon />
                </IconButton>
            </>
        );
    };

    return (
        <Modal open={open} sx={{ ...flexAllCentre, zIndex: 1100 }}>
            <Box sx={bcbsTcRetroModal}>
                <Grid container>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h5" sx={{ px: 2, mb: 3 }}>
                            The start of care date given is in the past more than one business
                            day. Please provide the number of visits by requested discipline
                            that fall within the dates below.
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }} sx={{ px: 2, mb: 1 }}>
                        <Grid container>
                            <Grid size={{ xs: 5.6 }}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={fontWeight[4]}
                                >
                                    Past Dates of Services
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6.4 }}>
                                <Typography variant="subtitle2">
                                    {formatDate(
                                        getValues("requestService.startOfCare"),
                                        DateFormats.MM_DD_YYYY
                                    )}
                                    {" - "}
                                    {formatDate(
                                        getValue(isRetroDetailsState, "effectiveEndDateMinOneDay"),
                                        DateFormats.MM_DD_YYYY,
                                        DateFormats.YYYY_MM_DD,
                                        true
                                    )}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Divider />
                <Grid container>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={RetroTableCellHeader}>
                                    Discipline(s) Selected
                                </TableCell>
                                <TableCell sx={RetroTableCellHeader}>
                                    Number of visits completed
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {selectedDisciplines.map((discipline, index) => (
                                <TableRow key={discipline.disciplineId} sx={{
                                    "& .MuiTableCell-root": {
                                        borderBottom: "none",
                                    },
                                }}>
                                    <TableCell sx={DisciplineCell}>
                                        {discipline.disciplineDescription}
                                    </TableCell>
                                    <TableCell sx={DisciplineCell}>
                                        {renderVisitInput(
                                            discipline.disciplineId,
                                            index
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Grid>
                <Grid
                    container
                    sx={{ pl: 2, mt: 3 }}
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {modalError && (
                        <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                            <Typography
                                variant="subtitle1"
                                color={colors.red[100]}
                                fontWeight={fontWeight[2]}
                            >
                                {modalError}
                            </Typography>
                        </Grid>
                    )}

                    <Grid>
                        <Button
                            variant="outlined"
                            sx={RetroCancelButton}
                            onClick={handleCloseModal}
                        >
                            CANCEL
                        </Button>
                        <Button
                            variant="contained"
                            sx={RetroConfirmButton}
                            onClick={handleConfirm}
                        >
                            CONFIRM
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
};

export default BcbsTnRetroModal;